/**
 * <infinite-scroll-carousel>
 *
 * An endlessly looping horizontal scroll carousel. The user can swipe / drag
 * in both directions and the items wrap seamlessly (last → first, first → last).
 * Optional auto-play scrolls continuously and pauses on interaction.
 *
 * Implementation notes:
 * - The element clones its children twice so the DOM contains three identical
 *   sets: [clones-A] [originals] [clones-B]. The initial scrollLeft is set to
 *   the start of [originals]. When the user (or auto-play) scrolls past the
 *   edges of [originals], we silently jump back by one set-width — invisible
 *   to the user because the visual content is identical.
 * - Cloned elements get `aria-hidden="true"` and `data-clone="true"` so
 *   assistive tech and any in-section JS can identify and skip them.
 * - Respects `prefers-reduced-motion: reduce` — disables auto-play.
 * - Single-instance safe: multiple of these on a page do not interfere.
 *
 * Attributes:
 *   auto-play                "true" to start auto-play on mount
 *   auto-play-speed          pixels per second (default 30)
 *   auto-play-resume-delay   ms to wait after user interaction before resuming (default 3000)
 *   pause-on-hover           "true" to pause when pointer enters (default false on touch)
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class InfiniteScrollCarousel extends HTMLElement {
  connectedCallback() {
    this._originals = Array.from(this.children);
    if (this._originals.length < 2) return;

    this._abort = new AbortController();
    this._userPaused = false;
    this._setupClones();
    this._initScrollPosition();
    this._bindEvents();
    this._setupMouseDrag();

    if (this.hasAttribute('auto-play') && this.getAttribute('auto-play') !== 'false' && !REDUCED_MOTION) {
      // Defer slightly so layout settles before auto-play starts
      requestAnimationFrame(() => this._startAutoPlay());
    }
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._stopAutoPlay();
    if (this._resumeTimer) clearTimeout(this._resumeTimer);
  }

  _setupClones() {
    // Two clone sets on each side (5 total: [cA1][cA2][O][cB1][cB2]) so the
    // wrap thresholds are reachable on viewports where viewport_width is
    // larger than one set of originals. With only 1 clone set each side the
    // right-side wrap could become physically unreachable (max scrollLeft
    // ends up below the wrap threshold), making "scroll right infinitely"
    // feel like a wall. Two sets each side keeps things symmetrical and
    // the DOM cost is still trivial (5 × number-of-cards nodes).
    const CLONE_SETS_PER_SIDE = 2;
    this._cloneSetsPerSide = CLONE_SETS_PER_SIDE;
    const fragBefore = document.createDocumentFragment();
    const fragAfter = document.createDocumentFragment();
    for (let s = 0; s < CLONE_SETS_PER_SIDE; s++) {
      this._originals.forEach((item) => {
        const a = item.cloneNode(true);
        const b = item.cloneNode(true);
        [a, b].forEach((clone) => {
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-clone', 'true');
          clone.setAttribute('tabindex', '-1');
          // Disable any focusable descendants inside the clone
          clone.querySelectorAll('a, button, input, [tabindex]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
        });
        fragBefore.appendChild(a);
        fragAfter.appendChild(b);
      });
    }
    this.insertBefore(fragBefore, this.firstChild);
    this.appendChild(fragAfter);
  }

  _initScrollPosition() {
    let initialized = false;

    // Measures dimensions and (on first call) lands on the originals block.
    // On subsequent calls (resize, font-load) we keep the current scroll
    // position but compensate it for the new layout — otherwise re-measuring
    // would either reset the user's position or break auto-play progress.
    const measure = () => {
      const totalSets = 1 + 2 * (this._cloneSetsPerSide || 1);
      const childCount = this.children.length;
      const setSize = childCount / totalSets;
      if (setSize !== Math.floor(setSize)) return;

      const originalsIndex = this._cloneSetsPerSide * setSize;
      const firstOriginal = this.children[originalsIndex];
      const firstAfter = this.children[originalsIndex + setSize];
      const newSetWidth = firstAfter.offsetLeft - firstOriginal.offsetLeft;
      const newOriginalStart = firstOriginal.offsetLeft;

      if (!initialized) {
        this._setWidth = newSetWidth;
        this._originalStart = newOriginalStart;
        this._silentJump(this._originalStart);
        initialized = true;
        return;
      }

      // Preserve the relative position within the originals block across
      // re-measures (e.g. on viewport resize).
      if (this._setWidth > 0) {
        const relativeProgress = (this.scrollLeft - this._originalStart) / this._setWidth;
        this._setWidth = newSetWidth;
        this._originalStart = newOriginalStart;
        this._silentJump(this._originalStart + relativeProgress * newSetWidth);
      } else {
        this._setWidth = newSetWidth;
        this._originalStart = newOriginalStart;
      }
    };

    // First measurement after the next paint
    requestAnimationFrame(measure);

    // Re-measure when fonts load, images load, or viewport changes
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    window.addEventListener('resize', () => requestAnimationFrame(measure), {
      signal: this._abort.signal,
      passive: true,
    });
  }

  _bindEvents() {
    let pendingRaf = false;
    this.addEventListener(
      'scroll',
      () => {
        if (pendingRaf) return;
        pendingRaf = true;
        requestAnimationFrame(() => {
          pendingRaf = false;
          this._checkBounds();
        });
      },
      { signal: this._abort.signal, passive: true }
    );

    // Pause auto-play on user interaction (touch, mouse, keyboard scroll)
    const pauseEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
    pauseEvents.forEach((ev) => {
      this.addEventListener(ev, () => this._pauseAutoPlay(), {
        signal: this._abort.signal,
        passive: true,
      });
    });

    if (this.getAttribute('pause-on-hover') === 'true') {
      this.addEventListener('pointerenter', () => this._pauseAutoPlay(true), {
        signal: this._abort.signal,
      });
      this.addEventListener('pointerleave', () => this._resumeAutoPlay(), {
        signal: this._abort.signal,
      });
    }
  }

  _checkBounds() {
    if (!this._setWidth) return;
    // Use a small buffer so the user can scroll a bit past the threshold before
    // we jump — feels less abrupt and avoids fighting momentum scrolling.
    const buffer = Math.min(this._setWidth * 0.05, 40);
    const lowerThreshold = this._originalStart - buffer;
    const upperThreshold = this._originalStart + this._setWidth + buffer;

    if (this.scrollLeft < lowerThreshold) {
      this._silentJump(this.scrollLeft + this._setWidth);
    } else if (this.scrollLeft >= upperThreshold) {
      this._silentJump(this.scrollLeft - this._setWidth);
    }
  }

  /**
   * Mouse drag-to-scroll. Touch is left to native scroll (works better with
   * momentum). We only intercept mouse pointers (and pen) and forward the
   * delta as scrollLeft updates.
   */
  _setupMouseDrag() {
    let isDown = false;
    let isDragging = false;
    let startX = 0;
    let startScroll = 0;
    let currentX = 0;
    let dragDistance = 0;
    let rafPending = false;
    let prevSnap = '';
    let prevBehavior = '';
    let capturedPointerId = null;
    const THRESHOLD = 5;

    // Apply the latest pointer delta — batched via rAF so we update at most
    // once per frame even if pointermove fires at 120+ Hz.
    const applyScroll = () => {
      rafPending = false;
      if (!isDragging) return;
      this.scrollLeft = startScroll - (currentX - startX);
      this._checkBounds();
    };

    // Promote a tracked pointerdown into an actual drag. Called from onMove
    // once we've crossed the THRESHOLD — NOT from onDown, so a pure click
    // (no movement before pointerup) never triggers pointer capture or
    // snap-locking. That means clicks on the play-button overlay (or other
    // interactive shadow-DOM controls inside cards) pass through normally.
    const beginDrag = (e) => {
      if (isDragging) return;
      isDragging = true;
      prevSnap = this.style.scrollSnapType;
      prevBehavior = this.style.scrollBehavior;
      this.style.scrollSnapType = 'none';
      this.style.scrollBehavior = 'auto';
      this.classList.add('is-dragging');
      this._pauseAutoPlay(true);
      try {
        this.setPointerCapture(e.pointerId);
        capturedPointerId = e.pointerId;
      } catch (_) {}
    };

    const onDown = (e) => {
      // Only hijack mouse / pen, never touch (native scroll is better there)
      if (e.pointerType === 'touch') return;
      // Don't even track drags that start on interactive children — let them
      // own the gesture entirely.
      if (e.target.closest('a, button, input, textarea, select, video, video-media')) return;
      isDown = true;
      isDragging = false;
      startX = currentX = e.clientX;
      startScroll = this.scrollLeft;
      dragDistance = 0;
      // Note: no pointer capture, no snap-lock, no auto-play pause yet.
      // Those happen lazily in beginDrag() once movement crosses THRESHOLD.
    };

    const onMove = (e) => {
      if (!isDown) return;
      currentX = e.clientX;
      dragDistance = Math.abs(currentX - startX);
      if (dragDistance > THRESHOLD) {
        beginDrag(e);
        e.preventDefault();
        if (!rafPending) {
          rafPending = true;
          requestAnimationFrame(applyScroll);
        }
      }
    };

    const onUp = (e) => {
      if (!isDown) return;
      isDown = false;
      rafPending = false;
      if (isDragging) {
        isDragging = false;
        this.style.scrollSnapType = prevSnap;
        this.style.scrollBehavior = prevBehavior;
        this.classList.remove('is-dragging');
        try { this.releasePointerCapture(capturedPointerId ?? e.pointerId); } catch (_) {}
        capturedPointerId = null;
        // Resume auto-play after the configured delay (if enabled)
        if (this.hasAttribute('auto-play') && this.getAttribute('auto-play') !== 'false' && !REDUCED_MOTION) {
          if (this._resumeTimer) clearTimeout(this._resumeTimer);
          const delay = parseInt(this.getAttribute('auto-play-resume-delay') || '3000', 10);
          this._resumeTimer = setTimeout(() => this._startAutoPlay(), delay);
        }
        // Suppress the click that follows a real drag (prevents accidental link nav)
        const swallow = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          this.removeEventListener('click', swallow, true);
        };
        this.addEventListener('click', swallow, true);
        setTimeout(() => this.removeEventListener('click', swallow, true), 50);
      }
      // If !isDragging here: it was a pure click — nothing to clean up,
      // the click event continues to its real target untouched.
    };

    this.addEventListener('pointerdown', onDown, { signal: this._abort.signal });
    this.addEventListener('pointermove', onMove, { signal: this._abort.signal });
    this.addEventListener('pointerup', onUp, { signal: this._abort.signal });
    this.addEventListener('pointercancel', onUp, { signal: this._abort.signal });
    // Prevent native drag-image on images inside the carousel
    this.addEventListener('dragstart', (e) => e.preventDefault(), { signal: this._abort.signal });
  }

  _silentJump(targetLeft) {
    const prev = this.style.scrollBehavior;
    this.style.scrollBehavior = 'auto';
    this.scrollLeft = targetLeft;
    // Force a reflow before restoring scroll-behavior so the jump is instant
    void this.offsetWidth;
    this.style.scrollBehavior = prev || '';
  }

  _startAutoPlay() {
    // _userPaused is the persistent, user-controlled stop (WCAG 2.2.2). While it
    // is set, no path — transient interaction-resume, hover-leave, drag-end — may
    // restart auto-play; only togglePause() clears it.
    if (this._autoPlayRaf || this._userPaused) return;
    const speed = parseFloat(this.getAttribute('auto-play-speed')) || 30;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      // Use silent scroll so we don't trigger smooth-scroll easing
      const prev = this.style.scrollBehavior;
      this.style.scrollBehavior = 'auto';
      this.scrollLeft += (speed * dt) / 1000;
      this.style.scrollBehavior = prev || '';
      this._checkBounds();
      this._autoPlayRaf = requestAnimationFrame(tick);
    };
    this._autoPlayRaf = requestAnimationFrame(tick);
  }

  _stopAutoPlay() {
    if (this._autoPlayRaf) {
      cancelAnimationFrame(this._autoPlayRaf);
      this._autoPlayRaf = null;
    }
  }

  _pauseAutoPlay(indefinite = false) {
    if (!this.hasAttribute('auto-play') || this.getAttribute('auto-play') === 'false') return;
    this._stopAutoPlay();
    if (this._resumeTimer) clearTimeout(this._resumeTimer);
    if (indefinite) return;
    const delay = parseInt(this.getAttribute('auto-play-resume-delay') || '3000', 10);
    this._resumeTimer = setTimeout(() => this._startAutoPlay(), delay);
  }

  _resumeAutoPlay() {
    if (!this.hasAttribute('auto-play') || this.getAttribute('auto-play') === 'false') return;
    if (REDUCED_MOTION) return;
    if (this._resumeTimer) clearTimeout(this._resumeTimer);
    this._startAutoPlay();
  }

  /**
   * Persistent user pause/play (WCAG 2.2.2). Public so a host pause button can
   * drive it. Returns the new state: true = now paused, false = now playing.
   * Once paused this way it stays paused (see the _userPaused guard in
   * _startAutoPlay) regardless of scroll/drag/hover interaction.
   */
  togglePause() {
    this._userPaused = !this._userPaused;
    if (this._userPaused) {
      this._stopAutoPlay();
      if (this._resumeTimer) clearTimeout(this._resumeTimer);
    } else {
      this._startAutoPlay();
    }
    return this._userPaused;
  }
}

if (!window.customElements.get('infinite-scroll-carousel')) {
  window.customElements.define('infinite-scroll-carousel', InfiniteScrollCarousel);
}
